/* District Bangle — gift-quiz.js
   The 5-question quiz state machine (Gift Quiz.dc.html):
     landing → question×N → analyzing → results  (+ no-result, error, retake)
   Rules-based, not AI. A month answer pins the match; otherwise the chosen
   palette (visual) provides a pool of month keys. The top match + alternatives
   are always REAL products; Add adds them at full price and the 20% is the
   merchant's automatic cart discount. We show a "Top match" badge, not a
   fabricated percentage. */
import { BaseElement, define, money, bus } from '@theme/global';

const ANALYZE_MSGS = [
  'Reading her color story…',
  'Matching a birthstone…',
  'Checking the fit (spoiler: it fits)…',
  'Wrapping it up…',
];

class GiftQuiz extends BaseElement {
  init() {
    this.pct = Number(this.dataset.pct) || 20;
    this.total = Number(this.dataset.total) || 0;
    this.fallback = (this.dataset.fallback || '').split(',').map((s) => s.trim()).filter(Boolean);
    try { this.stones = JSON.parse(this.querySelector('[data-stones]').textContent); } catch (_) { this.stones = []; }
    this.byKey = {}; this.stones.forEach((s) => { this.byKey[s.key] = s; });

    this.answers = {};        // qid -> value (or array for multi)
    this.qScreens = [...this.querySelectorAll('[data-screen="question"]')];
    this.progress = this.querySelector('[data-progress]');
    this.bar = this.querySelector('[data-bar]');
    this.plabel = this.querySelector('[data-plabel]');
    this.index = 0;

    this.listen(this.querySelector('[data-start]'), 'click', () => this.show(0));
    this.querySelectorAll('[data-back]').forEach((b) => this.listen(b, 'click', () => this.prev()));
    this.querySelectorAll('[data-restart]').forEach((b) => this.listen(b, 'click', () => this.restart()));

    this.qScreens.forEach((scr) => {
      const qid = scr.dataset.qid; const qtype = scr.dataset.qtype;
      scr.querySelectorAll('[data-opt]').forEach((opt) => this.listen(opt, 'click', () => {
        if (qtype === 'multi') {
          const set = new Set(this.answers[qid] || []);
          if (set.has(opt.dataset.value)) set.delete(opt.dataset.value); else set.add(opt.dataset.value);
          this.answers[qid] = [...set];
          opt.classList.toggle('is-on');
        } else {
          this.answers[qid] = { value: opt.dataset.value, extra: opt.dataset.extra || '' };
          scr.querySelectorAll('[data-opt]').forEach((o) => o.classList.toggle('is-on', o === opt));
          this.advance();
        }
      }));
      const next = scr.querySelector('[data-next]'); if (next) this.listen(next, 'click', () => this.advance());
      const skip = scr.querySelector('[data-skip]'); if (skip) this.listen(skip, 'click', () => { delete this.answers[qid]; this.advance(); });
    });
  }

  screen(name) {
    this.querySelectorAll('[data-screen]').forEach((s) => { s.hidden = s.dataset.screen !== name || (name === 'question'); });
  }

  show(i) {
    this.index = i;
    this.querySelectorAll('[data-screen]').forEach((s) => { s.hidden = true; });
    this.progress.hidden = false;
    const scr = this.qScreens[i];
    if (scr) scr.hidden = false;
    const pctDone = Math.round(((i) / this.total) * 100);
    if (this.bar) this.bar.style.width = `${pctDone}%`;
    if (this.plabel) this.plabel.textContent = `${i + 1} / ${this.total}`;
    scr?.querySelector('[data-opt]')?.focus();
  }

  advance() {
    if (this.index < this.total - 1) this.show(this.index + 1);
    else this.analyze();
  }

  prev() {
    if (this.index > 0) this.show(this.index - 1);
    else { this.progress.hidden = true; this.querySelectorAll('[data-screen]').forEach((s) => { s.hidden = s.dataset.screen !== 'landing'; }); }
  }

  analyze() {
    this.progress.hidden = true;
    this.querySelectorAll('[data-screen]').forEach((s) => { s.hidden = s.dataset.screen !== 'analyzing'; });
    const msg = this.querySelector('[data-analyze]');
    let n = 0;
    if (msg) { msg.textContent = ANALYZE_MSGS[0]; this._t = setInterval(() => { n = (n + 1) % ANALYZE_MSGS.length; msg.textContent = ANALYZE_MSGS[n]; }, 500); }
    setTimeout(() => {
      clearInterval(this._t);
      try { this.results(); } catch (e) { console.error(e); this.state('error'); }
    }, 1400);
  }

  state(name) {
    this.progress.hidden = true;
    this.querySelectorAll('[data-screen]').forEach((s) => { s.hidden = s.dataset.screen !== name; });
  }

  /* ---- matching -------------------------------------------------------
     Every question moves the result. This used to pin on the month and
     otherwise take the first palette entry, which meant the recipient,
     the budget and the "what matters" answers were collected and thrown
     away — five questions, two of them load-bearing. */
  poolFrom(qid) {
    const a = this.answers[qid];
    if (!a) return [];
    if (Array.isArray(a)) return a.map((s) => s.trim()).filter(Boolean);
    return (a.extra || '').split(',').map((s) => s.trim()).filter(Boolean);
  }

  ranked() {
    const monthA = this.answers.month && this.answers.month.value;
    const vibePool = this.poolFrom('vibe');
    const mattersPool = this.poolFrom('matters');
    const score = {};
    this.stones.forEach((s) => {
      let n = 0;
      if (monthA && s.key === monthA) n += 100;                        // her month outranks everything
      const vi = vibePool.indexOf(s.key);
      if (vi >= 0) n += 40 - vi * 6;                                   // palette, best first
      if (mattersPool.indexOf(s.key) >= 0) n += 15;                    // what she cares about
      n += Math.max(0, (parseFloat(s.rating) || 0) - 4) * 8;           // popularity, as a tiebreak
      const fi = this.fallback.indexOf(s.key);
      if (fi >= 0) n += Math.max(0, 6 - fi);                           // house favourites
      if (!s.vid) n -= 60;                                             // cannot be bought: must not lead
      score[s.key] = n;
    });
    return this.stones.slice().sort((a, b) =>
      (score[b.key] - score[a.key]) || ((b.reviews || 0) - (a.reviews || 0)));
  }

  recommend() { const r = this.ranked()[0]; return r && r.key; }

  altKeys(topKey) {
    return this.ranked().filter((s) => s.key !== topKey).slice(0, 3).map((s) => s.key);
  }

  /* how big a gift they said they wanted — drives which action leads */
  bundleSize() {
    const a = this.answers.budget;
    const v = a ? String(a.value || '').toLowerCase() : '';
    if (!v) return 1;
    if (/(^|\D)3|three|stack|statement|splash|big/.test(v)) return 3;
    if (/(^|\D)2|two|pair|mid/.test(v)) return 2;
    return 1;
  }

  buildReason(top) {
    const whoA = this.answers.who && this.answers.who.value;
    const who = whoA ? String(whoA).trim() : '';
    const bits = [];
    if (this.answers.month && this.answers.month.value === top.key) {
      bits.push(`${top.month} is her birth month`);
    }
    if (this.poolFrom('vibe').indexOf(top.key) >= 0) bits.push('it sits in the palette you chose');
    const matters = Array.isArray(this.answers.matters) ? this.answers.matters : [];
    if (matters.length) bits.push(`you said ${matters.slice(0, 2).join(' and ').toLowerCase()} matters most`);
    if (!bits.length) bits.push('it is the one most people land on');
    const lead = who ? `For ${who.toLowerCase()} — ` : '';
    return `${lead}chosen because ${bits.join(', ')}. One flexible size fits 5.5"\u2013 8.5", and it arrives gift-ready.`;
  }

  results() {
    const topKey = this.recommend();
    const top = this.byKey[topKey];
    if (!top) return this.state('none');

    const reason = this.buildReason(top);
    /* The budget answer decides which action leads: someone who said "a big
       moment" should not have to find the stack button underneath a single add. */
    const want = this.bundleSize();
    const altPreview = this.altKeys(topKey).map((k) => this.byKey[k]).filter(Boolean);
    const bundle = [top].concat(altPreview).slice(0, Math.max(1, want));
    const gross = bundle.reduce((t, s2) => t + (s2.price || 2500), 0);
    const net = bundle.length >= 3 ? gross - Math.round(gross * this.pct / 100) : gross;
    const single = `<button type="button" class="dbq__top-add" data-add-top>${top.vid ? 'Add to cart \u00b7 ' + money(top.price || 2500) : 'View bangle'}</button>`;
    const setBtn = `<button type="button" class="dbq__top-stack" data-add-stack>${
      bundle.length >= 3 ? 'Build 3-stack \u00b7 ' + money(net) + ' (save ' + this.pct + '%)'
                         : 'Add ' + bundle.length + '-piece set \u00b7 ' + money(net)}</button>`;
    const actions = want > 1 ? setBtn + single : single + setBtn;
    const stoneMedia = (s) => s.img ? `<img src="${s.img}" alt="${s.stone}">` : s.simg ? `<img class="is-stone" src="${s.simg}" alt="${s.stone}">` : `<span class="db-stone" style="--stone:${s.color};--glow:${s.glow}"></span>`;

    this.querySelector('[data-top]').innerHTML = `
      <div class="dbq__top-card">
        <div class="dbq__top-media">${stoneMedia(top)}<span class="dbq__top-badge">${window.theme.strings.topMatch || 'Top match'}</span></div>
        <div class="dbq__top-body">
          <div class="dbq__top-month">${top.month} birthstone</div>
          <h3 class="dbq__top-name">${top.stone} Bangle</h3>
          <div class="dbq__top-rate"><span style="color:var(--db-gold)">★★★★★</span> ${top.rating} · ${top.reviews} reviews</div>
          <div class="dbq__why"><div class="dbq__why-l">Why this match</div><div class="dbq__why-d">${reason}</div></div>
          <div class="dbq__top-price"><b>${money(top.price || 2500)}</b><span>fits 5.5"–8.5" · gift-ready</span></div>
          <div class="dbq__top-actions">${actions}</div>
        </div>
      </div>`;

    const alts = this.altKeys(topKey).map((k) => this.byKey[k]).filter(Boolean);
    this.querySelector('[data-alts]').innerHTML = alts.map((s) => `
      <div class="dbq__alt">
        <div class="dbq__alt-media">${stoneMedia(s)}</div>
        <div class="dbq__alt-body">
          <div class="dbq__alt-name">${s.stone}</div>
          <div class="dbq__alt-note">${s.month} birthstone</div>
          <div class="dbq__alt-foot"><span>${money(s.price || 2500)}</span><button type="button" class="dbq__alt-add" data-add-one="${s.vid || ''}">Add</button></div>
        </div>
      </div>`).join('');

    // wire result buttons
    this.topKey = topKey; this.altList = alts;
    this.querySelector('[data-add-top]')?.addEventListener('click', (e) => this.add([top], e.currentTarget, '✓ Added'));
    this.querySelector('[data-add-stack]')?.addEventListener('click', (e) => this.add(bundle, e.currentTarget, bundle.length >= 3 ? `\u2713 Stack added \u2014 ${this.pct}% applied` : '\u2713 Set added'));
    this.querySelectorAll('[data-add-one]').forEach((b) => b.addEventListener('click', () => {
      const s = alts.find((x) => String(x.vid) === b.dataset.addOne);
      if (s) this.add([s], b, '✓');
    }));

    this.state('results');
  }

  buildReason(top) {
    const who = this.answers.who && this.answers.who.value;
    const bits = [];
    if (this.answers.month && this.answers.month.value === top.key) bits.push(`it's her ${top.month} birthstone`);
    if (this.answers.vibe) bits.push('it fits the palette you picked');
    bits.push('one flexible size fits her, and it arrives gift-ready');
    return `Chosen because ${bits.join(', ')}.`;
  }

  async add(stones, btn, okLabel) {
    const items = stones.filter((s) => s && s.vid).map((s) => ({ id: s.vid, quantity: 1 }));
    if (!items.length) { if (this.topKey && this.byKey[this.topKey].url) window.location.href = this.byKey[this.topKey].url; return; }
    if (btn) btn.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(window.theme.routes.cart_add, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message);
      bus.emit('cart:updated', { open: true });
      if (btn) { btn.classList.add('is-added'); btn.textContent = okLabel; }
    } catch (e) { console.error(e); this.state('error'); }
    finally { if (btn) btn.removeAttribute('aria-busy'); }
  }

  restart() {
    this.answers = {};
    this.qScreens.forEach((s) => s.querySelectorAll('.is-on').forEach((o) => o.classList.remove('is-on')));
    this.progress.hidden = true;
    this.querySelectorAll('[data-screen]').forEach((s) => { s.hidden = s.dataset.screen !== 'landing'; });
  }
}

define('gift-quiz', GiftQuiz);
